import { CorePasswordAdminAccountRole, PasswordAdminAccountRequest } from '../models/CorePasswordAdminModels';
import { PasswordChangeSearchPageRequest } from '../models/CorePasswordChangeModels';
import {
    PageRequest,
    SearchPageRequest,
    UserScheduleSearchPageRequest,
    FilterModuleSearchPageRequest,
    GroupSearchPageRequest,
    ScheduleSyncEventSearchPageRequest,
    OneRosterClassSearchPageRequest,
    OneRosterUserSearchPageRequest,
    UserSearchPageRequest,
    InstanceLogSearchPageRequest,
    DnpActivitiesSearchPageRequest,
    AuditLogSearchPageRequest,
    DeviceSearchPageRequest,
    ClassroomAdminClassroomPageRequest,
} from '../models/Requests'

const parsePageRequest = (searchParams: URLSearchParams): Partial<PageRequest> => {
    const request: Partial<PageRequest> = {};

    const page = getNumber(searchParams, 'page');
    if (page !== null) {
        request.page = page;
    }

    const pageSize = getNumber(searchParams, 'pageSize');
    if (pageSize !== null) {
        request.pageSize = pageSize;
    }

    return request;
}

const parseClassroomAdminClassroomPageRequest = (searchParams: URLSearchParams): Partial<ClassroomAdminClassroomPageRequest> => {
    const request: Partial<ClassroomAdminClassroomPageRequest> = parseSearchPageRequest(searchParams);

    const schoolUnitId = searchParams.get('schoolUnitId');
    if (schoolUnitId !== null) {
        request.schoolUnitId = schoolUnitId;
    }

    const status = getNumber(searchParams, 'status');
    if (status !== null) {
        request.status = status;
    }
    const syncState = getNumber(searchParams, 'syncState');
    if (syncState !== null) {
        request.syncState = syncState;
    }

    const createdFrom = searchParams.get('createdFrom');
    if (createdFrom !== null) {
        request.createdFrom = createdFrom;
    }

    const createdTo = searchParams.get('createdTo');
    if (createdTo !== null) {
        request.createdTo = createdTo;
    }

    const missingTeachers = getBoolean(searchParams, 'missingTeachers');
    if (missingTeachers !== null) {
        request.missingTeachers = missingTeachers;
    }

    const missingStudents = getBoolean(searchParams, 'missingStudents');
    if (missingStudents !== null) {
        request.missingStudents = missingStudents;
    }

    return request;
}

const parseSearchPageRequest = (searchParams: URLSearchParams): Partial<SearchPageRequest> => {
    const request: Partial<SearchPageRequest> = parsePageRequest(searchParams);

    const search = searchParams.get('search');
    if (search !== null) {
        request.search = search;
    }

    const onlyActive = getBoolean(searchParams, 'onlyActive');
    if (onlyActive !== null) {
        request.onlyActive = onlyActive;
    }

    return request;
}

const parseFilterModuleSearchPageRequest = (searchParams: URLSearchParams): Partial<FilterModuleSearchPageRequest> => {
    const request: Partial<FilterModuleSearchPageRequest> = parseSearchPageRequest(searchParams);

    const moduleTypeName = searchParams.get('moduleTypeName')
    if (moduleTypeName !== null) {
        request.moduleTypeName = moduleTypeName;
    }

    return request;
}

const parseGroupSearchPageRequest = (searchParams: URLSearchParams): Partial<GroupSearchPageRequest> => {
    const request: Partial<GroupSearchPageRequest> = parseFilterModuleSearchPageRequest(searchParams);

    const groupType = getNumber(searchParams, 'groupType');
    if (groupType !== null) {
        request.groupType = groupType;
    }

    return request;
}

const parseUserSearchPageRequest = (searchParams: URLSearchParams): Partial<UserSearchPageRequest> => {
    const request: Partial<UserSearchPageRequest> = parseFilterModuleSearchPageRequest(searchParams);

    const roleId = getNumber(searchParams, 'roleId');
    if (roleId !== null) {
        request.roleId = roleId;
    }

    const roleType = getNumber(searchParams, 'roleType');
    if (roleType !== null) {
        request.roleType = roleType;
    }

    return request;
}

const parseUserSchedulePageRequest = (searchParams: URLSearchParams): Partial<UserScheduleSearchPageRequest> => {
    const request: Partial<UserScheduleSearchPageRequest> = parseSearchPageRequest(searchParams);

    const socialnumber = searchParams.get('socialnumber');
    if (socialnumber !== null) {
        request.socialnumber = socialnumber;
    }

    const role = searchParams.get('role');
    if (role !== null) {
        request.role = role;
    }

    return request;
}

const parseScheduleSyncEventSearchPageRequest = (searchParams: URLSearchParams): Partial<ScheduleSyncEventSearchPageRequest> => {
    const request: Partial<ScheduleSyncEventSearchPageRequest> = parseSearchPageRequest(searchParams);

    const onlyUpcomingDates = getBoolean(searchParams, 'onlyUpcomingDates');
    if (onlyUpcomingDates !== null) {
        request.onlyUpcomingDates = onlyUpcomingDates;
    }

    const targetSystem = getNumber(searchParams, 'targetSystem');
    if (targetSystem !== null) {
        request.targetSystem = targetSystem;
    }

    return request;
}

const parseOneRosterClassSearchPageRequest = (searchParams: URLSearchParams): Partial<OneRosterClassSearchPageRequest> => {
    const request: Partial<OneRosterClassSearchPageRequest> = parseSearchPageRequest(searchParams);

    const status = searchParams.get('status');
    if (status !== null) {
        request.status = status; 
    }

    return request;
}

const parseOneRosterUserSearchPageRequest = (searchParams: URLSearchParams): Partial<OneRosterUserSearchPageRequest> => {
    const request: Partial<OneRosterUserSearchPageRequest> = parseSearchPageRequest(searchParams);

    const role = searchParams.get('role');
    if (role !== null) {
        request.role = role;
    }

    return request;
}

const parseInstancelogSearchPageRequest = (searchParams: URLSearchParams): Partial<InstanceLogSearchPageRequest> => {
    const request: Partial<InstanceLogSearchPageRequest> = parsePageRequest(searchParams);

    const search = searchParams.get('search');
    if (search !== null) {
        request.search = search;
    }

    const severity = getNumber(searchParams, 'severity');
    if (severity !== null) {
        request.severity = severity;
    }

    const eventId = getNumber(searchParams, 'eventId');
    if (eventId !== null) {
        request.eventId = eventId;
    }

    return request;
}

const parseDnpActivitiesSearchPageRequest = (searchParams: URLSearchParams): Partial<DnpActivitiesSearchPageRequest> => {
    const request: Partial<DnpActivitiesSearchPageRequest> = parsePageRequest(searchParams);

    const filterType = searchParams.get('filterType');
    if (filterType !== null) {
        request.filterType = filterType;
    }

    const search = searchParams.get('search');
    if (search !== null) {
        request.search = search;
    }

    const syllabusId = searchParams.get('syllabusId');
    if (syllabusId !== null) {
        request.syllabusId = syllabusId;
    }

    const schoolType = searchParams.get('schoolType');
    if (schoolType !== null) {
        request.schoolType = schoolType;
    }

    const schoolYear = getNumbers(searchParams, 'schoolYear');
    if (schoolYear.length > 0) {
        schoolYear.forEach(x => {
            request.schoolYear = schoolYear;
        })
    } 

    const schoolUnitIds = getStrings(searchParams, 'schoolUnitIds');
    if (schoolUnitIds.length > 0) {
        schoolUnitIds.forEach(x => {
            request.schoolUnitIds = schoolUnitIds;
        })
    }

    return request;
}

const parseDeviceSearchPageRequest = (searchParams: URLSearchParams): Partial<DeviceSearchPageRequest> => {
    const request: Partial<DeviceSearchPageRequest> = parseSearchPageRequest(searchParams);

    const orgUnitPath = getStrings(searchParams, 'orgUnitPath');
    if (orgUnitPath.length > 0) {
        request.orgUnitPath = orgUnitPath;
    }

    const status = getNumbers(searchParams, 'status');
    if (status.length > 0) {
        request.status = status;
    }

    const lastSync = getNumber(searchParams, 'lastSync');
    if (lastSync !== null) {
        request.lastSync = lastSync;
    }

    const autoUpdateExpiration = searchParams.get('autoUpdateExpiration');
    if (autoUpdateExpiration !== null) {
        request.autoUpdateExpiration = autoUpdateExpiration;
    }

    const osVersion = getStrings(searchParams, 'osVersion');
    if (osVersion.length > 0) {
        request.osVersion = osVersion;
    }

    return request;
}

const parsePasswordAdminAccountRequest = (searchParams: URLSearchParams): PasswordAdminAccountRequest => {
    const searchPageRequest = parseSearchPageRequest(searchParams);

    const roleString = searchParams.get('role');
    let role = undefined;
    if (roleString === CorePasswordAdminAccountRole.STAFF.toString()) {
        role = CorePasswordAdminAccountRole.STAFF;
    } else if (roleString === CorePasswordAdminAccountRole.STUDENT.toString()) {
        role = CorePasswordAdminAccountRole.STUDENT;
    }
    
    return {
        ...searchPageRequest,
        schoolUnitId: searchParams.get('schoolUnitId') ?? undefined,
        classGroupId: searchParams.get('classGroupId') ?? undefined,
        showExported: getBoolean(searchParams, 'showExported') ?? undefined,
        role: role 
    };
}

const parsePasswordChangeSearchPageRequest = (searchParams: URLSearchParams): PasswordChangeSearchPageRequest => {
    const searchPageRequest = parseSearchPageRequest(searchParams);

    const roleString = searchParams.get('role');
    let role = undefined;
    if (roleString === CorePasswordAdminAccountRole.STAFF.toString()) {
        role = CorePasswordAdminAccountRole.STAFF;
    } else if (roleString === CorePasswordAdminAccountRole.STUDENT.toString()) {
        role = CorePasswordAdminAccountRole.STUDENT;
    }

    return {
        ...searchPageRequest,
        schoolUnitId: searchParams.get('schoolUnitId') ?? undefined,
        classGroupId: searchParams.get('classGroupId') ?? undefined,
        role: role
    };
}

const parseAuditLogSearchPageRequest = (searchParams: URLSearchParams): Partial<AuditLogSearchPageRequest> => {
    const request: Partial<AuditLogSearchPageRequest> = parsePageRequest(searchParams);

    const search = searchParams.get('search');
    if (search !== null) {
        request.search = search;
    }

    const eventId = getNumber(searchParams, 'eventId');
    if (eventId !== null) {
        request.eventId = eventId;
    }

    return request;
}

const getStrings = (searchParams: URLSearchParams, name: string): string[] => {
    const stringValues = searchParams.getAll(name);
    return stringValues.map(x => x);
} 

const getNumbers = (searchParams: URLSearchParams, name: string): number[] => {
    const stringValues = searchParams.getAll(name);
    return stringValues.map(x => parseInt(x, 10));
}

const getNumber = (searchParams: URLSearchParams, name: string): number | null => {
    const stringValue = searchParams.get(name);
    return stringValue ? parseInt(stringValue, 10) : null;
}

const getBoolean = (searchParams: URLSearchParams, name: string): boolean | null => {
    const stringValue = searchParams.get(name);
    return stringValue ? stringValue.toLowerCase() === 'true' : null;
}

export default {
    parsePageRequest,
    parseSearchPageRequest,
    parseFilterModuleSearchPageRequest,
    parseGroupSearchPageRequest,
    parseUserSearchPageRequest,
    parseUserSchedulePageRequest,
    parseScheduleSyncEventSearchPageRequest,
    parseOneRosterClassSearchPageRequest,
    parseOneRosterUserSearchPageRequest,
    parseInstancelogSearchPageRequest,
    parseDnpActivitiesSearchPageRequest,
    parsePasswordAdminAccountRequest,
    parsePasswordChangeSearchPageRequest,
    parseAuditLogSearchPageRequest,
    parseDeviceSearchPageRequest,
    parseClassroomAdminClassroomPageRequest,
}